import React, { ReactNode, useEffect, useRef } from 'react';
import 'PDF/components/Timeline/TimelineComponent.scss';

import './TimelineComponent.scss';
import { getColorType } from 'PDF/helpers/documentStyle';

import HistoricalBulbIcon from '../../assets/icons/HistoricalBulbIcon.svg';
import HistoricalBulbIconBlack from '../../assets/icons/HistoricalBulbIconBlack.svg';
import HistoricalCalendarIcon from '../../assets/icons/HistoricalCalendarIcon.svg';
import HistoricalCalendarIconBlack from '../../assets/icons/HistoricalCalendarIconBlack.svg';
import HistoricalDocumentsIcon from '../../assets/icons/HistoricalDocumentsIcon.svg';
import HistoricalDocumentsIconBlack from '../../assets/icons/HistoricalDocumentsIconBlack.svg';
import HistoricalPuzzleIcon from '../../assets/icons/HistoricalPuzzleIcon.svg';
import HistoricalPuzzleIconBlack from '../../assets/icons/HistoricalPuzzleIconBlack.svg';
import HistoricalShareIcon from '../../assets/icons/HistoricalShareIcon.svg';
import HistoricalShareIconBlack from '../../assets/icons/HistoricalShareIconBlack.svg';

interface ITimelineProps {
  className?: string;
  tip?: boolean;
  children: ReactNode;
}

const orderedTimelineImages = [
  HistoricalDocumentsIcon,
  HistoricalCalendarIcon,
  HistoricalPuzzleIcon,
  HistoricalBulbIcon,
  HistoricalShareIcon,
];

const orderedTimelineImagesBlack = [
  HistoricalDocumentsIconBlack,
  HistoricalCalendarIconBlack,
  HistoricalPuzzleIconBlack,
  HistoricalBulbIconBlack,
  HistoricalShareIconBlack,
];

const TimelineImagesByColorType = {
  green: orderedTimelineImages,
  blue: orderedTimelineImages,
  red: orderedTimelineImages,
  blueGreen: orderedTimelineImages,
  blueRed: orderedTimelineImages,
  blueYellow: orderedTimelineImagesBlack,
};

export const Timeline: React.FC<ITimelineProps> = ({ className, tip = true, children }) => {
  const tipClassName = tip ? 'with-tip' : '';
  const fullClassName = `timeline-wrapper ${tipClassName} ${className || ''}`;
  const numBookmarks = React.Children.count(children);
  const timelineRef = useRef<HTMLDivElement>(null);
  const styleRef = useRef<HTMLStyleElement | null>(null);

  const colorType = getColorType();

  useEffect(() => {
    if (!Number.isInteger(numBookmarks) || numBookmarks < 0 || numBookmarks > 100) {
      console.error('Invalid number of bookmarks');
      return;
    }

    if (!styleRef.current) {
      styleRef.current = document.createElement('style');
      styleRef.current.setAttribute('data-timeline-styles', '');
    }

    const cssRules = Array.from({ length: numBookmarks }, (_, i) => {
      const ruleIndex = i + 1;

      if (!Number.isInteger(ruleIndex) || ruleIndex < 1) return '';
      return `.timeline-item:nth-child(${ruleIndex}) { grid-row: ${ruleIndex}; }`;
    })
      .filter(Boolean)
      .join('\n');

    styleRef.current.textContent = cssRules;

    if (!styleRef.current.parentNode) {
      document.head.appendChild(styleRef.current);
    }

    return () => {
      if (styleRef.current?.parentNode) {
        styleRef.current.parentNode.removeChild(styleRef.current);
      }
      styleRef.current = null;
    };
  }, [numBookmarks]);

  useEffect(() => {
    const dotmarks = timelineRef.current?.querySelectorAll('.timeline-dotmark');
    if (dotmarks) {
      const lastDotmark = dotmarks[dotmarks.length - 1];
      lastDotmark?.querySelector('.timeline-dot')?.classList.add('no-after');
    }
  }, [children]);

  return (
    <div className={fullClassName} ref={timelineRef}>
      <div className="timeline-connector" />
      <ul className="timeline">
        {children}
        {Array(numBookmarks)
          .fill(null)
          .map((_, i) => (
            <span key={i} className="timeline-dotmark">
              <div
                className={`timeline-dot ${i === numBookmarks - 1 ? ((i + 1) % 2 == 1 ? 'last-timeline-dot-odd' : 'last-timeline-dot-even') : ''}`}
              >
                <div className="border">
                  <img src={TimelineImagesByColorType[colorType][i]} />
                </div>
              </div>
            </span>
          ))}
      </ul>
    </div>
  );
};
